// Onboarding — First-visit loading sequence with progressive reveal
//
// Timeline:
//   0–500ms    Screen dark, logo fades in
//   500–1500ms Grid 3D starts appearing (CSS class triggers canvas opacity)
//   1500–2500ms Rest of content fades in progressively
//   >2500ms    Site fully interactive

const STORAGE_KEY = 'onboarding-done';

const MILESTONES = {
    logo: 500,
    grid: 1500,
    content: 2500,
} as const;

function isHomePage(): boolean {
    const path = window.location.pathname;
    return path === '/' || path === '/index.html' || path === '/en' || path === '/en/' || path === '/fr' || path === '/fr/';
}

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function alreadyCompleted(): boolean {
    try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        // sessionStorage may be unavailable (private browsing, etc.)
        return false;
    }
}

function markCompleted(): void {
    try {
        sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
        // Silently ignore storage errors
    }
}

export function initOnboarding(): void {
    if (!isHomePage() || alreadyCompleted() || prefersReducedMotion()) {
        return;
    }

    const body = document.body;
    body.classList.add('onboarding-active');

    const start = performance.now();

    let logoApplied = false;
    let gridApplied = false;
    let contentApplied = false;

    function step(now: number): void {
        const elapsed = now - start;

        if (!logoApplied && elapsed >= MILESTONES.logo) {
            body.classList.add('onboarding-logo');
            logoApplied = true;
        }

        if (!gridApplied && elapsed >= MILESTONES.grid) {
            body.classList.add('onboarding-grid');
            gridApplied = true;
        }

        if (!contentApplied && elapsed >= MILESTONES.content) {
            body.classList.add('onboarding-content');
            contentApplied = true;
        }

        if (logoApplied && gridApplied && contentApplied) {
            // Allow transitions to finish, then clean up
            setTimeout(() => {
                body.classList.remove('onboarding-active');
                body.classList.add('onboarding-done');
                markCompleted();
            }, 800);
            return;
        }

        requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
}
